import { Link } from "react-router-dom";
import { ArrowUpRight, Download, ShieldCheck, FileText, Users, ClipboardCheck } from "lucide-react";
import Seo from "../components/site/Seo";
import { PageHeader, SectionLabel } from "../components/site/Section";
import { Reveal } from "../components/site/Reveal";

const CAPS = [
  { icon: FileText, title: "Proposal Staffing Support", body: "Contingent hire commitments, resumes, and key personnel identification to strengthen competitive federal bids." },
  { icon: ShieldCheck, title: "Cleared Talent Pipelines", body: "Access to cleared and clearable candidates across confidential, secret, and top-secret requirements." },
  { icon: Users, title: "Surge & Ramp Staffing", body: "Rapid mobilization of qualified personnel to meet award timelines and period-of-performance demands." },
  { icon: ClipboardCheck, title: "Compliance Alignment", body: "Processes mapped to FAR, OFCCP, and contract-specific reporting and vetting requirements." },
];

export default function Government() {
  return (
    <div data-testid="government-page">
      <Seo title="Government Solutions" description="Government and defense workforce solutions: proposal staffing, cleared talent pipelines, and FAR-aligned compliance from VectorBridge Group LLC." />
      <PageHeader
        label="G/01"
        kicker="Public Sector"
        title="Workforce solutions for the federal mission."
        intro="We support government agencies and defense contractors across the contract lifecycle — from capture and proposal through execution and surge — with talent aligned to the highest compliance standards."
      />

      <section className="relative overflow-hidden border-b border-border">
        <img src="https://images.unsplash.com/photo-1688417486337-b089fa4ee670?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxnb3Zlcm5tZW50JTIwZGVmZW5zZSUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzg0NjQ5ODUzfDA&ixlib=rb-4.1.0&q=85" alt="Capitol" className="absolute inset-0 h-full w-full object-cover opacity-20 grayscale" />
        <div className="absolute inset-0 bg-background/75" />
        <div className="relative mx-auto max-w-[1400px] px-5 py-20 sm:px-8 sm:py-28">
          <SectionLabel index="G/02">Core Capabilities</SectionLabel>
          <div className="mt-14 grid gap-px bg-border sm:grid-cols-2">
            {CAPS.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.title} delay={(i % 2) * 0.06} className="bg-background p-8 sm:p-10">
                  <Icon size={26} className="text-brand" />
                  <h3 className="mt-8 font-display text-2xl tracking-tight">{c.title}</h3>
                  <p className="mt-3 text-muted-foreground">{c.body}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-b border-border py-20 sm:py-28">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 sm:px-8 md:grid-cols-12">
          <div className="md:col-span-5">
            <SectionLabel index="G/03">Past Performance Ready</SectionLabel>
            <Reveal><h2 className="mt-8 font-display text-4xl font-light leading-tight tracking-tighter sm:text-5xl">Documentation your acquisition team expects.</h2></Reveal>
          </div>
          <div className="md:col-span-7">
            <div className="grid gap-px bg-border">
              {["NAICS & SIC code alignment", "Core competencies & differentiators", "Points of contact & CAGE / UEI ready", "References & representative engagements"].map((t, i) => (
                <Reveal key={t} delay={i * 0.05} className="flex items-center justify-between gap-4 bg-background p-6">
                  <span>{t}</span>
                  <span className="font-mono text-xs text-brand">{String(i + 1).padStart(2, "0")}</span>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.1}>
              <a href="/VectorBridge-Capability-Statement.pdf" download data-testid="government-capability-download" className="group mt-8 inline-flex items-center gap-3 border border-border px-7 py-5 font-mono text-xs uppercase tracking-[0.15em] transition-colors hover:border-brand hover:text-brand">
                <Download size={18} /> Download Capability Statement
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-secondary">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-6 px-5 py-16 sm:flex-row sm:items-center sm:px-8">
          <h2 className="max-w-xl font-display text-3xl font-light tracking-tighter sm:text-4xl">Teaming on an upcoming bid? Let's align early.</h2>
          <Link to="/contact" data-testid="government-cta" className="group inline-flex items-center gap-2 bg-brand px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy">
            Contact contracts <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
